import React from 'react';
import { Tag, TagLabel, TagLeftIcon, Tooltip } from '@chakra-ui/react';
import { RiCheckboxCircleFill, RiErrorWarningFill, RiQuestionFill } from 'react-icons/ri';
import { ConnectionType, GatewayHealth } from '../../generated/graphql';

interface GatewayHealthTagProps {
  connectionType?: ConnectionType | null;
  gatewayHealth?: GatewayHealth | null;
  gatewayHealthReason?: string | null;
  size?: 'sm' | 'md' | 'lg';
}

const HEALTH_CONFIG: Record<
  GatewayHealth,
  { colorScheme: string; label: string; icon: typeof RiQuestionFill }
> = {
  [GatewayHealth.Healthy]: {
    colorScheme: 'green',
    label: 'Gateway: OK',
    icon: RiCheckboxCircleFill,
  },
  [GatewayHealth.Unreachable]: {
    colorScheme: 'red',
    label: 'Gateway: down',
    icon: RiErrorWarningFill,
  },
  [GatewayHealth.Unknown]: {
    colorScheme: 'gray',
    label: 'Gateway: unknown',
    icon: RiQuestionFill,
  },
};

/**
 * Shows the health of a connector agent's LOCAL gateway (the thing that actually
 * serves requests), separate from connectorStatus (the cloud link). An agent can
 * be ONLINE in the cloud while its local gateway is unreachable — this tag makes
 * that visible, with the failure reason in a tooltip.
 *
 * Renders nothing for non-CONNECTOR agents.
 */
export const GatewayHealthTag: React.FC<GatewayHealthTagProps> = ({
  connectionType,
  gatewayHealth,
  gatewayHealthReason,
  size = 'sm',
}) => {
  if (connectionType !== ConnectionType.Connector) {
    return null;
  }

  const health = gatewayHealth ?? GatewayHealth.Unknown;
  const config = HEALTH_CONFIG[health];

  const tag = (
    <Tag colorScheme={config.colorScheme} size={size} borderRadius="full">
      <TagLeftIcon as={config.icon} />
      <TagLabel>{config.label}</TagLabel>
    </Tag>
  );

  const tooltipLabel =
    gatewayHealthReason ||
    (health === GatewayHealth.Unknown
      ? 'No recent gateway health report from the connector.'
      : undefined);

  return tooltipLabel ? (
    <Tooltip label={tooltipLabel} hasArrow placement="top" maxW="340px">
      {tag}
    </Tooltip>
  ) : (
    tag
  );
};

export default GatewayHealthTag;
